import { ladderCanReachPlan } from '@devizy/shared';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { CsvExportButton, QuotePdfButton } from '@/components/export';
import { ProjectSummaryClient } from '@/components/project/project-summary-client';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/projects/$id/')({
  component: ProjectDetailPage,
});

function ProjectDetailPage(): React.ReactElement {
  const { id } = Route.useParams();
  const meQuery = trpc.user.me.useQuery();
  const projectQuery = trpc.project.byId.useQuery({ projectId: id });
  const planQuery = trpc.billing.currentPlan.useQuery();
  const officialQuoteQuery = trpc.project.officialQuoteId.useQuery({ projectId: id });

  if (projectQuery.isLoading || meQuery.isLoading) {
    return <p className="text-muted-foreground text-sm">Chargement…</p>;
  }
  if (projectQuery.isError || !projectQuery.data || !meQuery.data) {
    return <ProjectNotFound />;
  }

  const project = projectQuery.data;
  const userId = meQuery.data.id;
  const isRequester = project.requesterId === userId;
  const isBidder = project.bidderId === userId;
  if (!isRequester && !isBidder) {
    return <ProjectNotFound />;
  }

  const isBusiness = planQuery.data?.plan === 'BUSINESS';
  const canReachBusiness = isBidder && ladderCanReachPlan('BIDDER', 'BUSINESS');
  const officialQuoteId = officialQuoteQuery.data?.quoteId ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {officialQuoteId ? <QuotePdfButton quoteId={officialQuoteId} state="OFFICIAL" /> : null}
        {canReachBusiness ? (
          <CsvExportButton
            kind="project"
            projectId={project.id}
            disabled={!isBusiness}
            disabledReason={isBusiness ? null : 'BUSINESS_PLAN_REQUIRED'}
          />
        ) : null}
      </div>
      <ProjectSummaryClient projectId={project.id} isRequester={isRequester} />
    </div>
  );
}

function ProjectNotFound(): React.ReactElement {
  return (
    <div className="border-border bg-card/50 rounded-2xl border border-dashed p-10 text-center">
      <p className="text-muted-foreground text-sm">Projet introuvable.</p>
    </div>
  );
}
